import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { assetNavigationHref } from '../utils/assetOpener';
import TargetAudienceSelector from '../components/TargetAudienceSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MATERIAL_TYPES = [
  { value: 'diet_plan', label: 'Diet Plan' },
  { value: 'nutrition_guide', label: 'Nutrition Guide' },
  { value: 'pdf', label: 'PDF Document' },
  { value: 'video', label: 'Video' },
  { value: 'worksheet', label: 'Worksheet' },
  { value: 'progress_tracker', label: 'Progress Tracker' },
  { value: 'exercise', label: 'Exercise / Activity' },
  { value: 'other', label: 'Other' }
];

const StaffWellness = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('nutrition');
  
  // Dialog states
  const [materialDialog, setMaterialDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [viewMaterialDialog, setViewMaterialDialog] = useState(false);
  const [viewTaskDialog, setViewTaskDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [newMaterial, setNewMaterial] = useState({
    title: '',
    description: '',
    category: 'nutrition',
    material_type: '',
    content: '',
    target_type: 'all',
    target_athletes: [],
    target_squads: [],
    is_required: false
  });
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'nutrition',
    material_id: '',
    due_date: '',
    target_type: 'all',
    target_athletes: [],
    target_squads: [],
    instructions: ''
  });
  
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    if (!['Admin', 'Staff'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [materialsRes, tasksRes, athletesRes, squadsRes] = await Promise.all([
        axios.get(`${API}/wellness/materials`),
        axios.get(`${API}/wellness/tasks?include_completed=true`),
        axios.get(`${API}/athletes`),
        axios.get(`${API}/squads`)
      ]);
      setMaterials(materialsRes.data);
      setTasks(tasksRes.data);
      setAthletes(athletesRes.data);
      // Handle squads response - it returns {squads: [...]}
      setSquads(squadsRes.data.squads || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/wellness/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFile({
        url: response.data.file_url,
        name: response.data.file_name
      });
      setNewMaterial(prev => ({
        ...prev,
        file_url: response.data.file_url,
        file_name: response.data.file_name
      }));
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateMaterial = async () => {
    if (!newMaterial.title || !newMaterial.material_type) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/wellness/materials`, {
        ...newMaterial,
        category: activeTab
      });
      toast.success('Material created successfully');
      setMaterialDialog(false);
      resetMaterialForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create material');
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      await axios.post(`${API}/wellness/tasks`, {
        ...newTask,
        category: activeTab
      });
      toast.success('Task created successfully');
      setTaskDialog(false);
      resetTaskForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    
    try {
      await axios.delete(`${API}/wellness/materials/${materialId}`);
      toast.success('Material deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await axios.delete(`${API}/wellness/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const resetMaterialForm = () => {
    setNewMaterial({
      title: '',
      description: '',
      category: activeTab,
      material_type: '',
      content: '',
      target_type: 'all',
      target_athletes: [],
      target_squads: [],
      is_required: false
    });
    setUploadedFile(null);
  };

  const resetTaskForm = () => {
    setNewTask({
      title: '',
      description: '',
      category: activeTab,
      material_id: '',
      due_date: '',
      target_type: 'all',
      target_athletes: [],
      target_squads: [],
      instructions: ''
    });
  };

  const viewMaterial = async (material) => {
    try {
      const response = await axios.get(`${API}/wellness/materials/${material.id}`);
      setSelectedItem(response.data);
      setViewMaterialDialog(true);
    } catch (error) {
      toast.error('Failed to fetch material details');
    }
  };

  const viewTask = async (task) => {
    try {
      const response = await axios.get(`${API}/wellness/tasks/${task.id}`);
      setSelectedItem(response.data);
      setViewTaskDialog(true);
    } catch (error) {
      toast.error('Failed to fetch task details');
    }
  };

  const getMaterialTypeLabel = (type) => {
    return MATERIAL_TYPES.find(t => t.value === type)?.label || type;
  };

  const filterByCategory = (items, category) => {
    return items.filter(item => item.category === category);
  };

  if (!['Admin', 'Staff'].includes(user?.role)) return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Performance & Wellness
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Manage nutrition plans, mental training, and athlete wellness</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center mb-6">
            <TabsList>
              <TabsTrigger value="nutrition" className="gap-2">
                <span>🥗</span> Nutrition & Diet
              </TabsTrigger>
              <TabsTrigger value="mental_training" className="gap-2">
                <span>🧠</span> Mental Training
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => {
                  resetTaskForm();
                  setTaskDialog(true);
                }}
                data-testid="new-task-btn"
              >
                + New Task
              </Button>
              <Button 
                className="bg-pfap-green hover:bg-pfap-green/90"
                onClick={() => {
                  resetMaterialForm();
                  setMaterialDialog(true);
                }}
                data-testid="new-material-btn"
              >
                + Upload Material
              </Button>
            </div>
          </div>

          {['nutrition', 'mental_training'].map(category => (
            <TabsContent key={category} value={category}>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Materials Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Materials & Resources</CardTitle>
                      <CardDescription>
                        {filterByCategory(materials, category).length} items uploaded
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {filterByCategory(materials, category).length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-500 text-center py-8">No materials uploaded yet</p>
                      ) : (
                        <div className="space-y-3">
                          {filterByCategory(materials, category).map(material => (
                            <div 
                              key={material.id}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:bg-gray-700"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                  {material.material_type === 'video' ? '🎬' : 
                                   material.material_type === 'pdf' ? '📄' :
                                   material.material_type === 'diet_plan' ? '🍽️' :
                                   material.material_type === 'exercise' ? '🏃' : '📋'}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{material.title}</p>
                                  <div className="text-xs text-gray-500 dark:text-gray-500 flex items-center">
                                    <span>{getMaterialTypeLabel(material.material_type)}</span>
                                    {material.is_required && <Badge className="ml-2 bg-red-100 text-red-700">Required</Badge>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => viewMaterial(material)}>
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-600"
                                  onClick={() => handleDeleteMaterial(material.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tasks Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Tasks & Assignments</CardTitle>
                      <CardDescription>
                        {filterByCategory(tasks, category).length} active tasks
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {filterByCategory(tasks, category).length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-500 text-center py-8">No tasks created yet</p>
                      ) : (
                        <div className="space-y-3">
                          {filterByCategory(tasks, category).map(task => (
                            <div 
                              key={task.id}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:bg-gray-700"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-700">
                                  ✓
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{task.title}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-500">
                                    {task.due_date ? `Due: ${task.due_date}` : 'No due date'}
                                    {task.completion_count > 0 && (
                                      <Badge className="ml-2" variant="outline">
                                        {task.completion_count} completed
                                      </Badge>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => viewTask(task)}>
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-600"
                                  onClick={() => handleDeleteTask(task.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Create Material Dialog */}
        <Dialog open={materialDialog} onOpenChange={setMaterialDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Material</DialogTitle>
              <DialogDescription>
                Add a new {activeTab === 'nutrition' ? 'nutrition' : 'mental training'} resource
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={newMaterial.title}
                  onChange={e => setNewMaterial({...newMaterial, title: e.target.value})}
                  placeholder="Material title"
                  data-testid="material-title"
                />
              </div>
              
              <div>
                <Label>Type *</Label>
                <Select 
                  value={newMaterial.material_type}
                  onValueChange={v => setNewMaterial({...newMaterial, material_type: v})}
                >
                  <SelectTrigger data-testid="material-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MATERIAL_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newMaterial.description}
                  onChange={e => setNewMaterial({...newMaterial, description: e.target.value})}
                  placeholder="Brief description of this material"
                />
              </div>
              
              <div>
                <Label>Upload File</Label>
                <Input 
                  type="file" 
                  onChange={handleFileUpload}
                  disabled={uploading}
                  accept=".pdf,.mp4,.mov,.avi,.png,.jpg,.jpeg,.doc,.docx,.xlsx,.pptx"
                />
                {uploading && <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Uploading...</p>}
                {uploadedFile && (
                  <p className="text-sm text-emerald-600 mt-1">✓ {uploadedFile.name}</p>
                )}
              </div>
              
              <div>
                <Label>Or Add Text Content</Label>
                <Textarea 
                  value={newMaterial.content}
                  onChange={e => setNewMaterial({...newMaterial, content: e.target.value})}
                  placeholder="Enter text content directly..."
                  rows={4}
                />
              </div>
              
              <div>
                <TargetAudienceSelector
                  athletes={athletes}
                  value={{ target_type: newMaterial.target_type || 'all', target_squads: newMaterial.target_squads, target_athletes: newMaterial.target_athletes }}
                  onChange={(val) => setNewMaterial({...newMaterial, target_type: val.target_type, target_squads: val.target_squads, target_athletes: val.target_athletes})}
                  label="Target Audience"
                  data-testid="material-target-audience"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox"
                  id="is_required"
                  checked={newMaterial.is_required}
                  onChange={e => setNewMaterial({...newMaterial, is_required: e.target.checked})}
                />
                <Label htmlFor="is_required" className="cursor-pointer">
                  Mark as required (athletes must acknowledge)
                </Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setMaterialDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateMaterial} className="bg-pfap-green hover:bg-pfap-green/90">
                Create Material
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Task Dialog */}
        <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Task</DialogTitle>
              <DialogDescription>
                Assign a new {activeTab === 'nutrition' ? 'nutrition' : 'mental training'} task
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={newTask.title}
                  onChange={e => setNewTask({...newTask, title: e.target.value})}
                  placeholder="Task title"
                  data-testid="task-title"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newTask.description}
                  onChange={e => setNewTask({...newTask, description: e.target.value})}
                  placeholder="What should athletes do?"
                />
              </div>
              
              <div>
                <Label>Instructions</Label>
                <Textarea 
                  value={newTask.instructions}
                  onChange={e => setNewTask({...newTask, instructions: e.target.value})}
                  placeholder="Step-by-step instructions..."
                />
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input 
                  type="date"
                  value={newTask.due_date}
                  onChange={e => setNewTask({...newTask, due_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Link to Material (optional)</Label>
                <Select 
                  value={newTask.material_id || 'none'}
                  onValueChange={v => setNewTask({...newTask, material_id: v === 'none' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No linked material" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked material</SelectItem>
                    {filterByCategory(materials, activeTab).map(mat => (
                      <SelectItem key={mat.id} value={mat.id}>{mat.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <TargetAudienceSelector
                  athletes={athletes}
                  value={{ target_type: newTask.target_type || 'all', target_squads: newTask.target_squads, target_athletes: newTask.target_athletes }}
                  onChange={(val) => setNewTask({...newTask, target_type: val.target_type, target_squads: val.target_squads, target_athletes: val.target_athletes})}
                  label="Target Audience"
                  data-testid="task-target-audience"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setTaskDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateTask} className="bg-pfap-green hover:bg-pfap-green/90">
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Material Dialog */}
        <Dialog open={viewMaterialDialog} onOpenChange={setViewMaterialDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedItem?.title}</DialogTitle>
              <DialogDescription>
                {getMaterialTypeLabel(selectedItem?.material_type)}
              </DialogDescription>
            </DialogHeader>
            
            {selectedItem && (
              <div className="space-y-4 py-4">
                {selectedItem.description && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Description</Label>
                    <p className="text-gray-700 dark:text-gray-300">{selectedItem.description}</p>
                  </div>
                )}
                
                {selectedItem.content && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Content</Label>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg whitespace-pre-wrap">
                      {selectedItem.content}
                    </div>
                  </div>
                )}
                
                {selectedItem.file_url && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Attached File</Label>
                    <a 
                      href={assetNavigationHref(selectedItem.file_url, selectedItem.file_name || selectedItem.title)}
                      data-testid="staff-material-file-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:underline flex items-center gap-2"
                    >
                      📎 {selectedItem.file_name || 'Download file'}
                    </a>
                  </div>
                )}
                
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  Created by {selectedItem.created_by_name} on {new Date(selectedItem.created_at).toLocaleDateString()}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Task Dialog */}
        <Dialog open={viewTaskDialog} onOpenChange={setViewTaskDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedItem?.title}</DialogTitle>
              {selectedItem?.due_date && (
                <DialogDescription>Due: {selectedItem.due_date}</DialogDescription>
              )}
            </DialogHeader>
            
            {selectedItem && (
              <div className="space-y-4 py-4">
                {selectedItem.description && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Description</Label>
                    <p className="text-gray-700 dark:text-gray-300">{selectedItem.description}</p>
                  </div>
                )}
                
                {selectedItem.instructions && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Instructions</Label>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg whitespace-pre-wrap">
                      {selectedItem.instructions}
                    </div>
                  </div>
                )}
                
                {selectedItem.material && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Linked Material</Label>
                    <p className="text-emerald-600">{selectedItem.material.title}</p>
                  </div>
                )}
                
                {selectedItem.completions && selectedItem.completions.length > 0 && (
                  <div>
                    <Label className="text-gray-500 dark:text-gray-500">Completions ({selectedItem.completions.length})</Label>
                    <div className="space-y-2 mt-2">
                      {selectedItem.completions.map(comp => (
                        <div key={comp.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 p-2 rounded">
                          <span className="text-sm">{comp.athlete_name}</span>
                          <Badge className={comp.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 dark:bg-gray-700'}>
                            {comp.is_completed ? 'Completed' : 'Pending'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StaffWellness;
